import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useLayoutEffect, useState } from 'react'

// FIXME: Циклическая зависимость
// eslint-disable-next-line import/no-internal-modules
import { usePersistence } from '../model/Persistence'
import { useEventListener } from './useEventListener'
import { useLatest } from './useLatest'

/**
 * Хранилище в соответствии с настройками персистентности
 *
 * @param key ключ для storage
 * @param convertFromStorage функция десериализации строки в желаемый тип
 * @param convertToStorage функция сериализация желаемого типа в строку
 * @param initialValue значение по умолчанию
 * @returns аналогично useState
 */
export const useStorage = <T>(
  key: string,
  convertFromStorage: (item: string | null) => T,
  convertToStorage: (item: T) => string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const storage = usePersistence()
  const readValue = useCallback(() => {
    try {
      const fromStorage = storage.getItem(key)
      return fromStorage ? convertFromStorage(fromStorage) : initialValue
    } catch (error) {
      console.error(error)
      return initialValue
    }
  }, [convertFromStorage, initialValue, key, storage])

  const [storedValue, setStoredValue] = useState<T>(readValue)
  const storedLatest = useLatest(storedValue)

  const setItem = useCallback<Dispatch<SetStateAction<T>>>(
    value => {
      const oldValue = storedLatest.current
      const newValue = value instanceof Function ? value(oldValue) : value

      storage.setItem(key, convertToStorage(newValue))
      setStoredValue(newValue)
      window.dispatchEvent(
        new StorageEvent('external-storage', {
          key,
          newValue: convertToStorage(newValue),
          oldValue: convertToStorage(oldValue),
        }),
      )
    },
    [convertToStorage, key, storage],
  )

  useLayoutEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key !== key) return

      setStoredValue(readValue())
    },
    [key, readValue],
  )

  useEventListener('storage', handleStorageChange)
  useEventListener('external-storage', handleStorageChange)

  return [storedValue, setItem]
}

export {}

declare global {
  interface WindowEventHandlersEventMap {
    ['external-storage']: StorageEvent
  }
}
